import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalParticipant } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Mic, MicOff, Video, VideoOff, SwitchCamera, Settings2, Share2, Flag, Power, Hand, LogOut, Users, Target, Radio, LayoutDashboard } from "lucide-react";
import LiveRoomRequestCohostButton from "@/components/live-room/LiveRoomRequestCohostButton";

const MIN_BTN_H = 46;
const MIN_BTN_W = 46;
const HEADER_H = 20;

function useCellSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

// Picks a grid that keeps every button >= 46px tall/wide so icons + labels
// stay readable in any cell (iPhone portrait band, 3×2 sixth slot, desktop).
function planGrid(n, w, h) {
  if (!w || !h) return { cols: Math.min(n, 3), rows: Math.ceil(n / 3), header: true, labels: true, btnH: 48, colW: 80, short: false };
  const rowsFit = Math.max(1, Math.floor((h + 6) / (MIN_BTN_H + 6)));
  const maxCols = Math.max(2, Math.floor((w + 6) / (MIN_BTN_W + 6)));
  const colsMin = Math.min(maxCols, w >= 260 ? 3 : 2);
  const cols = Math.min(maxCols, Math.max(colsMin, Math.ceil(n / rowsFit)));
  const rows = Math.ceil(n / cols);
  // Title only when it doesn't squeeze buttons below the readable minimum.
  const header = h - HEADER_H - (rows - 1) * 6 >= rows * MIN_BTN_H;
  const avail = h - (header ? HEADER_H : 0);
  const btnH = (avail - (rows - 1) * 6) / rows;
  const colW = (w - (cols - 1) * 6) / cols;
  return { cols, rows, header, labels: btnH >= 38, btnH, colW, short: colW < 62 };
}

// Shorter words for narrow columns (phone portrait band / landscape phone).
const SHORT = { Requests: "Guests", Settings: "Tools", "End Live": "End", Camera: "Cam", "Mic on": "Mic", "Leave box": "Leave" };

function Btn({ testid, onClick, active, danger, label, Icon, disabled, badge, labels, big, short, style }) {
  const text = short ? (SHORT[label] || label) : label;
  return (
    <button
      type="button"
      data-testid={testid}
      aria-label={label}
      title={label}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      disabled={disabled}
      style={style}
      className={`relative w-full h-full flex flex-col items-center justify-center gap-0.5 rounded-xl border font-semibold leading-none transition-colors min-h-0 px-0.5 disabled:opacity-40 ${big ? "text-[11px]" : short ? "text-[9px]" : "text-[10px]"} ${
        danger ? "bg-rose-600 border-rose-400/70 text-white hover:bg-rose-500 shadow-[0_0_14px_-4px_rgba(244,63,94,0.8)]"
          : active === false ? "bg-rose-500/25 border-rose-400/50 text-rose-100"
          : "bg-white/[0.12] border-white/20 text-white hover:bg-white/20"}`}
    >
      <Icon size={big ? 20 : 17} strokeWidth={2.2} />
      {labels && <span className="whitespace-nowrap max-w-full overflow-hidden text-ellipsis">{text}</span>}
      {badge > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-amber-400 text-black text-[9px] font-bold flex items-center justify-center" data-testid={`${testid}-badge`}>{badge}</span>}
    </button>
  );
}

function useAv() {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();
  const [facing, setFacing] = useState("user");
  const [flipping, setFlipping] = useState(false);
  const toggleMic = useCallback(() => localParticipant?.setMicrophoneEnabled(!isMicrophoneEnabled).catch(() => {}), [localParticipant, isMicrophoneEnabled]);
  const toggleCam = useCallback(() => localParticipant?.setCameraEnabled(!isCameraEnabled).catch(() => {}), [localParticipant, isCameraEnabled]);
  const flip = useCallback(async () => {
    if (!localParticipant || flipping) return;
    const next = facing === "user" ? "environment" : "user";
    setFlipping(true);
    try {
      const track = localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      if (track?.restartTrack) await track.restartTrack({ facingMode: next });
      else { await localParticipant.setCameraEnabled(false); await localParticipant.setCameraEnabled(true, { facingMode: next }); }
      setFacing(next);
    } catch { /* single camera */ } finally { setFlipping(false); }
  }, [localParticipant, facing, flipping]);
  return { isMicrophoneEnabled, isCameraEnabled, toggleMic, toggleCam, flip, flipping, ready: !!localParticipant };
}

function Head({ title, Icon, show }) {
  if (!show) return null;
  return (
    <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] text-rose-300 mb-1.5 leading-none" style={{ height: HEADER_H - 6 }}>
      <Icon size={11} /> <span className="truncate">{title}</span>
    </div>
  );
}

function ButtonGrid({ items, plan }) {
  const n = items.length;
  return (
    <div className="grid gap-1.5 flex-1 min-h-0" style={{ gridTemplateColumns: `repeat(${plan.cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${plan.rows}, minmax(0, 1fr))` }}>
      {items.map((it, i) => {
        const last = i === n - 1;
        const rem = n % plan.cols;
        const span = last && rem ? plan.cols - rem + 1 : 1;
        return <Btn key={it.testid} {...it} labels={plan.labels} short={plan.short && span === 1} big={plan.btnH >= 60 && plan.colW >= 80} style={span > 1 ? { gridColumn: `span ${span}` } : undefined} />;
      })}
    </div>
  );
}

// Sixth cell of the stage (2–5 publishers, portrait + landscape).
// Host → Creator Control Center · guest → own AV + leave · viewer → room info card.
export default function CreatorControlCenterCell({ cc, isHost, canPublish }) {
  const av = useAv();
  const c = cc || {};
  const ref = useRef(null);
  const { w, h } = useCellSize(ref);

  if (isHost) {
    const items = [
      { testid: "ccc-mic", onClick: av.toggleMic, active: av.isMicrophoneEnabled, Icon: av.isMicrophoneEnabled ? Mic : MicOff, label: av.isMicrophoneEnabled ? "Mic on" : "Mic off", disabled: !av.ready },
      { testid: "ccc-camera", onClick: av.toggleCam, active: av.isCameraEnabled, Icon: av.isCameraEnabled ? Video : VideoOff, label: av.isCameraEnabled ? "Camera" : "Cam off", disabled: !av.ready },
      { testid: "ccc-flip", onClick: av.flip, Icon: SwitchCamera, label: "Flip", disabled: !av.ready || av.flipping || !av.isCameraEnabled },
      { testid: "ccc-requests", onClick: c.onOpenRequests, Icon: Hand, label: "Requests", badge: c.requestsCount || 0 },
      { testid: "ccc-settings", onClick: c.onOpenSettings, Icon: Settings2, label: "Settings" },
      { testid: "ccc-share", onClick: c.onShare, Icon: Share2, label: "Share" },
      { testid: "ccc-report", onClick: c.onReport, Icon: Flag, label: "Report" },
      ...(c.onOpenAdmin ? [{ testid: "ccc-admin", onClick: c.onOpenAdmin, Icon: LayoutDashboard, label: "Admin" }] : []),
      { testid: "ccc-end-live", onClick: c.onEndStream, Icon: Power, label: "End Live", danger: true },
    ];
    const plan = planGrid(items.length, w, h);
    return (
      <div ref={ref} className="relative w-full h-full bg-zinc-950 border border-white/15 rounded-sm p-1.5 flex flex-col min-h-0 overflow-hidden" data-testid="live-creator-control-center" data-cols={plan.cols} data-labels={plan.labels ? "1" : "0"} onClick={(e) => e.stopPropagation()}>
        <Head title={w < 240 ? "Control Center" : "Creator Control Center"} Icon={Radio} show={plan.header} />
        <ButtonGrid items={items} plan={plan} />
      </div>
    );
  }

  if (canPublish) {
    const items = [
      { testid: "ccc-mic", onClick: av.toggleMic, active: av.isMicrophoneEnabled, Icon: av.isMicrophoneEnabled ? Mic : MicOff, label: av.isMicrophoneEnabled ? "Mic on" : "Mic off", disabled: !av.ready },
      { testid: "ccc-camera", onClick: av.toggleCam, active: av.isCameraEnabled, Icon: av.isCameraEnabled ? Video : VideoOff, label: av.isCameraEnabled ? "Camera" : "Cam off", disabled: !av.ready },
      { testid: "ccc-flip", onClick: av.flip, Icon: SwitchCamera, label: "Flip", disabled: !av.ready || av.flipping || !av.isCameraEnabled },
      { testid: "ccc-leave-box", onClick: c.onLeaveBox, Icon: LogOut, label: "Leave box", danger: true },
    ];
    const plan = planGrid(items.length, w, h);
    return (
      <div ref={ref} className="relative w-full h-full bg-zinc-950 border border-white/15 rounded-sm p-1.5 flex flex-col min-h-0 overflow-hidden" data-testid="live-guest-control-cell" onClick={(e) => e.stopPropagation()}>
        <Head title="Your box" Icon={Radio} show={plan.header} />
        <ButtonGrid items={items} plan={plan} />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-zinc-950/95 border border-white/10 rounded-sm p-3 flex flex-col min-h-0 overflow-hidden" data-testid="live-room-info-cell" onClick={(e) => e.stopPropagation()}>
      <Head title="Live room" Icon={Radio} show />
      <div className="text-sm font-semibold text-white truncate" data-testid="room-info-host">{c.hostName || "Host"}</div>
      <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-1"><Users size={12} /> <span data-testid="room-info-viewers">{(c.viewerCount ?? 0).toLocaleString()}</span> watching</div>
      {c.goal?.coins > 0 && (
        <div className="mt-2" data-testid="room-info-goal">
          <div className="flex items-center justify-between text-[10px] text-zinc-400"><span className="flex items-center gap-1"><Target size={10} /> {c.goal.label || "Room goal"}</span><span>{Math.min(100, Math.round(((c.goal.progress || 0) / c.goal.coins) * 100))}%</span></div>
          <div className="h-1.5 bg-white/10 rounded-full mt-1"><div className="h-full bg-rose-500 rounded-full" style={{ width: `${Math.min(100, ((c.goal.progress || 0) / c.goal.coins) * 100)}%` }} /></div>
        </div>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-2">
        {c.canRequestJoin && c.streamId && c.userId && <LiveRoomRequestCohostButton streamId={c.streamId} userId={c.userId} />}
        <button type="button" data-testid="ccc-share" onClick={(e) => { e.stopPropagation(); c.onShare?.(); }} className="h-9 px-3 rounded-full bg-white/5 border border-white/10 text-white text-[11px] font-semibold flex items-center gap-1"><Share2 size={13} /> Share</button>
        <button type="button" data-testid="ccc-report" onClick={(e) => { e.stopPropagation(); c.onReport?.(); }} className="h-9 px-3 rounded-full bg-white/5 border border-white/10 text-white text-[11px] font-semibold flex items-center gap-1"><Flag size={13} /> Report</button>
      </div>
    </div>
  );
}
